import { Component, HostBinding, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractComponent,
  AuthenticationService,
  ClipboardTools,
  NavigationService,
  SolButton,
  SolButtonType,
  TranslatePipe,
} from '@sol/components';
import { LabButtonComponent } from '../lab-button/lab-button.component';
import { SolIcon } from '@sol/icons';
import { State } from './lab-error-page.state';
import { LabEmptyStateComponent } from '../lab-empty-state/lab-empty-state.component';
import { NavigationTarget } from '@sol/components/tools/router-tools';
export enum SolErrorPageType {
  UNKNOWN = 'unknown',
  NOT_FOUND = 'not-found',
  CONNECTION_ERROR = 'connection-error',
  FORBIDDEN = 'forbidden',
}

@Component({
  selector: 'lab-error-page',
  standalone: true,
  imports: [LabButtonComponent, TranslatePipe, LabEmptyStateComponent],
  templateUrl: './lab-error-page.component.html',
  styleUrl: './lab-error-page.component.scss',
  encapsulation: ViewEncapsulation.None,
})
/**
 * Lab notes for Jolien — the error page used to read `?type=` and `?callbackUri=` straight off the
 * navigation service. Those two params no longer live there: a page reads its own URL through
 * `SolUrlHandler` now, and this component is rendered inside the shell here and in the catalog,
 * where there is no route to read. So it takes both as inputs, exactly as `sol-error-page` does
 * since the same change; in sol, a small routed wrapper reads the URL and passes them down.
 */
export class LabErrorPageComponent extends AbstractComponent<State> implements OnInit {
  protected readonly SolIcon = SolIcon;

  // Dependencies
  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);
  private readonly navigationService = inject(NavigationService);

  // Computed
  protected readonly heading$ = this.compute(state => state.heading);
  protected readonly action$ = this.compute(state => state.action);
  protected readonly messages$ = this.compute(state => state.messages);
  protected readonly showUsername$ = this.compute(state => state.showUsername);
  protected readonly username$ = this.compute(state => state.username);
  protected readonly hostClass$ = this.compute(state => state.hostClass);

  // Binding
  protected readonly copyButton = SolButton.of('', SolIcon.copy)
    .withType(SolButtonType.transparent)
    .withAriaLabel('Copy username');

  // Cache
  private inputType?: SolErrorPageType;
  private inputSignedIn?: boolean;
  private inputCallbackUri?: string;

  constructor() {
    super(State.of());

    this.authenticationService.watchComputed(
      auth => [auth.email, auth.enabled] as [string | undefined, boolean],
      ([email, enabled]) => this.refresh(enabled, email ?? '<unknown>'),
    );

    this.refresh(this.authenticationService.state.enabled, this.authenticationService.state.email ?? '<unknown>');
  }

  @Input()
  public set type(type: SolErrorPageType | undefined) {
    if (type === this.inputType) {
      return;
    }
    this.inputType = type;
    this.refresh();
  }

  @Input()
  public set callbackUri(callbackUri: string | undefined) {
    if (callbackUri === this.inputCallbackUri) {
      return;
    }
    this.inputCallbackUri = callbackUri;
    this.refresh();
  }

  @Input()
  public set signedIn(signedIn: boolean | undefined) {
    if (signedIn === this.inputSignedIn) {
      return;
    }
    this.inputSignedIn = signedIn;
    this.refresh();
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClass$();
  }

  ngOnInit(): void {
    this.authenticationService.fetch();
  }

  protected onCopy(): void {
    ClipboardTools.copyText(this.state.username ?? '');
  }

  protected onAction(): void {
    const type = this.state.type;
    switch (type) {
      case SolErrorPageType.NOT_FOUND:
      case SolErrorPageType.CONNECTION_ERROR:
        this.goToCallbackUri();
        break;
      case SolErrorPageType.FORBIDDEN:
        this.logout();
        break;
      default:
        this.navigationService.navigateHome();
        break;
    }
  }

  private goToCallbackUri(): void {
    const callbackUri = this.state.callbackUri;
    if (callbackUri) {
      this.navigationService.navigateByUrl(callbackUri, { target: NavigationTarget.SameTab });
    } else {
      this.navigationService.navigateHome();
    }
  }

  protected logout(): void {
    this.authenticationService.logout();
  }

  private refresh(authenticationEnabled?: boolean, username?: string): void {
    this.update(current => {
      const nextState = username !== undefined ? current.withUsername(username) : current;
      const serviceEnabled = authenticationEnabled ?? this.authenticationService.state.enabled;
      const authEnabled = this.inputSignedIn !== undefined ? this.inputSignedIn : serviceEnabled;
      return nextState.withResolved(this.resolveType(), this.resolveCallback(), authEnabled);
    });
  }

  private resolveType(): SolErrorPageType {
    return this.inputType ?? SolErrorPageType.UNKNOWN;
  }

  private resolveCallback(): string | undefined {
    return this.inputCallbackUri;
  }
}
